import { supabase } from '../../lib/supabase';
import { Meeting } from '../../types/meetings';
import { convertToMeeting } from './utils';
import { trackMeetingCreation, trackMeetingAnalysis } from '../../utils/analytics';

export const meetingsService = {
  async getMeetings(userId: string) {
    const { data, error } = await supabase
      .from('meetings')
      .select(`
        *,
        meeting_notes!meeting_notes_meeting_id_fkey (
          summary
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching meetings:', error);
      throw error;
    }

    // Fetch action items count in a separate query
    const meetingsWithCounts = await Promise.all(
      data.map(async (meeting) => {
        const { count } = await supabase
          .from('action_items')
          .select('*', { count: 'exact', head: true })
          .eq('meeting_id', meeting.id);

        const convertedMeeting = convertToMeeting(meeting);
        return {
          ...convertedMeeting,
          summary: meeting.meeting_notes?.[0]?.summary || '',
          action_items_count: count || 0
        };
      })
    );

    return meetingsWithCounts;
  },

  async getMeeting(meetingId: string, userId: string) {
    const { data: meeting, error: meetingError } = await supabase
      .from('meetings')
      .select(`
        *,
        meeting_notes!meeting_notes_meeting_id_fkey (
          summary,
          key_points,
          sentiment
        )
      `)
      .eq('id', meetingId)
      .eq('user_id', userId)
      .maybeSingle();

    if (meetingError) {
      console.error('Error fetching meeting:', meetingError);
      throw meetingError;
    }

    if (!meeting) {
      throw new Error('Meeting not found');
    }

    // Then fetch action items separately
    const { data: actionItems, error: actionItemsError } = await supabase
      .from('action_items')
      .select('*')
      .eq('meeting_id', meetingId)
      .order('created_at', { ascending: false });

    if (actionItemsError) {
      console.error('Error fetching action items:', actionItemsError);
      throw actionItemsError;
    }

    // Convert and combine the results
    return convertToMeeting({
      ...meeting,
      action_items: actionItems || []
    });
  },

  async createMeeting(userId: string, meetingData: Partial<Meeting>) {
    const { data, error } = await supabase
      .from('meetings')
      .insert({
        ...meetingData,
        user_id: userId,
        status: meetingData.status || 'pending',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;
    
    // Track meeting creation
    trackMeetingCreation(data.id, data.type || 'standard');
    
    return convertToMeeting(data);
  },

  async updateMeeting(meetingId: string, userId: string, updates: Partial<Meeting>) {
    const { data: updatedMeeting, error: updateError } = await supabase
      .from('meetings')
      .update({
        ...updates,
        tags: Array.isArray(updates.tags) ? updates.tags.join(',') : updates.tags,
        updated_at: new Date().toISOString()
      })
      .eq('id', meetingId)
      .eq('user_id', userId)
      .select()
      .single();

    if (updateError) throw updateError;

    // Track analysis completion if status is updated to 'completed'
    if (updates.status === 'completed') {
      trackMeetingAnalysis(meetingId, 'completed');
    }

    return convertToMeeting(updatedMeeting);
  },

  async deleteMeeting(meetingId: string, userId: string) {
    const { error } = await supabase
      .from('meetings')
      .delete()
      .eq('id', meetingId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting meeting:', error);
      throw error;
    }
  }
};